"use client";

import { IMAGE_REMOTE_HOSTS } from "@/lib/image-remote-hosts";
import { cn } from "@/lib/utils";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { LuCamera } from "react-icons/lu";

const allowedRemoteHosts = new Set<string>(IMAGE_REMOTE_HOSTS);

const canUseNextImage = (src: string): boolean => {
  if (!src) return false;

  if (
    src.startsWith("/") ||
    src.startsWith("blob:") ||
    src.startsWith("data:")
  ) {
    return true;
  }

  try {
    const url = new URL(src);
    if (url.protocol !== "https:" && url.protocol !== "http:") {
      return false;
    }

    return allowedRemoteHosts.has(url.hostname);
  } catch {
    return false;
  }
};

interface FileInputProfileProps {
  name: string;
  label?: string;
  className?: string;
  circleSize?: string; // ej. "w-32 h-32"
  iconSize?: string; // ej. "h-8 w-8"
  previewClassName?: string;
  required?: boolean;
  fallbackSrc?: string; // <-- nueva prop
}

export const FileInputProfile: React.FC<FileInputProfileProps> = ({
  name,
  label,
  className,
  circleSize = "w-32 h-32",
  iconSize = "h-8 w-8",
  previewClassName,
  required = true,
  fallbackSrc, // <-- la usamos
}) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control });

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [value]);

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = e.target.files;
    onChange(files && files.length > 0 ? files[0] : null);
  };

  const handleClick = () => inputRef.current?.click();

  let profileContent: React.ReactNode = (
    <LuCamera className={cn("text-gray-400", iconSize)} />
  );

  if (previewUrl) {
    profileContent = (
      <Image
        src={previewUrl}
        width={0}
        height={0}
        alt="Preview"
        className={cn(
          "absolute inset-0 w-full h-full object-cover",
          previewClassName
        )}
      />
    );
  } else if (fallbackSrc) {
    if (canUseNextImage(fallbackSrc)) {
      profileContent = (
        <Image
          src={fallbackSrc}
          fill
          alt="Default profile"
          className={cn(
            "absolute inset-0 w-full h-full object-cover",
            previewClassName,
            circleSize
          )}
          sizes="(max-width: 768px) 8rem, 12rem"
        />
      );
    } else {
      profileContent = (
        <img
          src={fallbackSrc}
          alt="Default profile"
          className={cn(
            "absolute inset-0 w-full h-full object-cover",
            previewClassName,
            circleSize
          )}
          loading="lazy"
        />
      );
    }
  }

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label className="block text-sm text-gray-600 font-semibold">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div
        className={cn(
          "relative flex items-center justify-center rounded-full bg-gray-100 border-2 border-dashed border-gray-300 cursor-pointer overflow-hidden",
          circleSize,
          error ? "border-red-500" : "hover:border-gray-400"
        )}
        onClick={handleClick}
        role="button"
        title="Click to upload image"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleClick();
        }}
      >
        <input
          type="file"
          ref={inputRef}
          onChange={handleFileChange}
          accept="image/*"
          className="hidden"
        />

        {profileContent}
      </div>

      {error && <p className="text-xs text-red-500">{error.message}</p>}
    </div>
  );
};
